/**
 * Requirements on the facts (CAPABILITIES.md §3.3): who asks, the merchant's configuration, and
 * where the app runs. Each is a few lines; a new kind of condition is one more builder here.
 */
import type { LicenceCode, MerchantConfig, ModuleCode, PermissionCode, PlatformName, Requirement, Surface } from "../types";
import { requirement } from "./requirement";

/** the subject holds this permission code */
export const permission = (code: PermissionCode): Requirement =>
  requirement(`permission:${code}`, "capabilities.permission", (facts) => facts.subject.permissions.has(code));

/**
 * The session's token carries this module; the server derived it from licence and role (§3.6).
 * Named `permittedModule`, not `module`: every CommonJS wrapper, Metro's included, binds `module`.
 */
export const permittedModule = (code: ModuleCode): Requirement =>
  requirement(`module:${code}`, "capabilities.module", (facts) => facts.subject.modules.has(code));

/** only for the choices the server does not make (§3.6): ask for a module everywhere else */
export const licence = (...codes: LicenceCode[]): Requirement =>
  requirement(`licence:${codes.join("|")}`, "capabilities.licence", (facts) => facts.subject.licence !== null && codes.includes(facts.subject.licence));

/** a merchant setting is on, or passes a test */
export const config = <K extends keyof MerchantConfig>(key: K, predicate: (value: MerchantConfig[K]) => boolean = Boolean): Requirement =>
  requirement(`config:${String(key)}`, "capabilities.config", (facts) => predicate(facts.tenant.config[key]));

/** asked from one of these screens; only for a rule that truly differs by page (§3.4) */
export const surface = (...surfaces: Surface[]): Requirement =>
  requirement(`surface:${surfaces.join("|")}`, "capabilities.surface", (facts) => surfaces.includes(facts.env.surface));

export const platform = (...platforms: PlatformName[]): Requirement =>
  requirement(`platform:${platforms.join("|")}`, "capabilities.platform", (facts) => platforms.includes(facts.env.platform));

export const online = (): Requirement => requirement("online", "capabilities.offline", (facts) => facts.env.online);

/** the device is in kiosk mode */
export const kioskMode = (): Requirement => requirement("kioskMode", "capabilities.kiosk", (facts) => facts.env.kiosk);
